// Modal dodawania/edycji usługi w ofercie handlowca.
// Tryby: 'add' (radio: ze słownika / własna nazwa) i 'edit' (formularz z aktualnymi wartościami).

import React, { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';

import { WiseModal } from 'components/wise';
import {
  useGetServicesDictionary,
  usePostSalesCrmOfferService,
  usePutSalesCrmOfferService,
  type IServiceDictionaryItem,
  type ISalesCrmOfferService,
  type OfferServicePriceType
} from 'api';

import styles from 'theme/components/containers/TraderClientOfferServicesContainer/AddOfferServiceModal.module.scss';

type Mode = 'add' | 'edit';
type SourceMode = 'dictionary' | 'adHoc';

interface IAddOfferServiceModalProps {
  open: boolean;
  mode: Mode;
  clientId: number;
  offerId: string;
  /** Pozycja oferty, do której dodajemy/edytujemy usługę. */
  positionId: number;
  /** Wymagane dla mode='edit' — wartości aktualnej usługi. */
  editingService?: ISalesCrmOfferService | null;
  onClose: () => void;
  onSaved?: () => void;
}

const DEBOUNCE_MS = 300;
const DICTIONARY_LIMIT = 20;
const DEFAULT_TAX_PERCENT = 23;

const AddOfferServiceModal: FC<IAddOfferServiceModalProps> = ({
  open,
  mode,
  clientId,
  offerId,
  positionId,
  editingService,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation();

  // ====== Form state ======
  const initialSourceMode: SourceMode = useMemo(() => {
    if (mode === 'edit' && editingService) {
      return editingService.serviceId != null ? 'dictionary' : 'adHoc';
    }
    return 'dictionary';
  }, [mode, editingService]);

  const [sourceMode, setSourceMode] = useState<SourceMode>(initialSourceMode);
  const [serviceId, setServiceId] = useState<number | null>(editingService?.serviceId ?? null);
  const [name, setName] = useState<string>(editingService?.name ?? '');
  const [priceNet, setPriceNet] = useState<number>(editingService?.priceNet ?? 0);
  const [taxPercent, setTaxPercent] = useState<number>(
    editingService?.taxPercent ?? DEFAULT_TAX_PERCENT
  );
  const [priceType, setPriceType] = useState<OfferServicePriceType>(
    editingService?.priceType ?? 'TOTAL'
  );

  // ====== Search w słowniku ======
  const [searchInput, setSearchInput] = useState<string>(
    mode === 'edit' && editingService?.serviceId != null ? editingService.name : ''
  );
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset formularza przy każdej zmianie `open` lub `editingService`.
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && editingService) {
      setSourceMode(editingService.serviceId != null ? 'dictionary' : 'adHoc');
      setServiceId(editingService.serviceId);
      setName(editingService.name);
      setPriceNet(editingService.priceNet);
      setTaxPercent(editingService.taxPercent);
      setPriceType(editingService.priceType);
      setSearchInput(editingService.serviceId != null ? editingService.name : '');
    } else {
      setSourceMode('dictionary');
      setServiceId(null);
      setName('');
      setPriceNet(0);
      setTaxPercent(DEFAULT_TAX_PERCENT);
      setPriceType('TOTAL');
      setSearchInput('');
    }
    setErrorMessage(null);
  }, [open, mode, editingService]);

  // Debounce wyszukiwania w słowniku (300ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: dictionaryData, isFetching: isDictionaryFetching } = useGetServicesDictionary(
    debouncedSearch,
    DICTIONARY_LIMIT,
    { enabled: open && sourceMode === 'dictionary' }
  );
  const dictionaryItems: IServiceDictionaryItem[] = dictionaryData?.items ?? [];

  const selectedDictionaryItem = useMemo(
    () => dictionaryItems.find((s) => s.id === serviceId) ?? null,
    [dictionaryItems, serviceId]
  );

  // ====== Mutacje ======
  const { mutate: addService, isLoading: isAdding } = usePostSalesCrmOfferService(
    clientId,
    offerId,
    positionId,
    {
      onSuccess: () => {
        onSaved?.();
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error?.message || t('Nie udało się dodać usługi.'));
      }
    }
  );

  const { mutate: putService, isLoading: isUpdating } = usePutSalesCrmOfferService(
    clientId,
    offerId,
    positionId,
    {
      onSuccess: () => {
        onSaved?.();
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error?.message || t('Nie udało się zaktualizować usługi.'));
      }
    }
  );

  const isSubmitting = isAdding || isUpdating;

  // ====== Handlers ======
  const handleSelectDictionaryItem = (item: IServiceDictionaryItem) => {
    setServiceId(item.id);
    setName(item.name);
    // Tylko gdy słownik ma wartość VAT — w przeciwnym razie zachowaj aktualną (domyślnie 23).
    if (item.taxPercent != null) {
      setTaxPercent(item.taxPercent);
    }
  };

  const handleSourceModeChange = (next: SourceMode) => {
    setSourceMode(next);
    if (next === 'adHoc') {
      setServiceId(null);
    } else {
      // Czyścimy własną nazwę gdy wracamy do słownika (zostanie nadpisana wyborem).
      setName('');
    }
    setErrorMessage(null);
  };

  const validate = (): string | null => {
    if (sourceMode === 'dictionary' && (serviceId == null || serviceId <= 0)) {
      return t('Wybierz usługę ze słownika.');
    }
    if (sourceMode === 'adHoc' && name.trim().length === 0) {
      return t('Podaj nazwę usługi.');
    }
    if (priceNet == null || Number.isNaN(priceNet) || priceNet < 0) {
      return t('Cena netto musi być liczbą większą lub równą 0.');
    }
    if (taxPercent == null || Number.isNaN(taxPercent) || taxPercent < 0 || taxPercent > 100) {
      return t('Stawka VAT musi być w zakresie 0-100.');
    }
    return null;
  };

  const handleSubmit = () => {
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setErrorMessage(null);

    const payload = {
      serviceId: sourceMode === 'dictionary' ? serviceId : null,
      // W trybie 'edit' zawsze wysyłamy `name` (snapshot ze stanu aktualnego — handlowiec
      // mógł go zmodyfikować lokalnie). W 'add' + 'adHoc' wysyłamy własną nazwę,
      // w 'add' + 'dictionary' wysyłamy null (backend pobierze nazwę ze Service).
      name:
        mode === 'edit'
          ? name
          : sourceMode === 'adHoc'
            ? name
            : null,
      priceNet,
      priceGross: null,
      taxPercent,
      priceType
    };

    if (mode === 'edit' && editingService) {
      putService({ id: editingService.id, payload });
    } else {
      addService(payload);
    }
  };

  const title =
    mode === 'edit' ? t('Edytuj usługę pozycji') : t('Dodaj usługę do pozycji');
  const submitLabel = mode === 'edit' ? t('Zapisz') : t('Dodaj');

  return (
    <WiseModal open={open} onClose={onClose} title={title} maxWidth={620}>
      <div className={styles.body}>
        {mode === 'add' && (
          <div className={styles.modeRow}>
            <div className={styles.modeLabel}>{t('Tryb dodawania')}</div>
            <RadioGroup
              row
              value={sourceMode}
              onChange={(_, value) => handleSourceModeChange(value as SourceMode)}>
              <FormControlLabel
                value="dictionary"
                control={<Radio size="small" />}
                label={t('Ze słownika')}
              />
              <FormControlLabel
                value="adHoc"
                control={<Radio size="small" />}
                label={t('Własna usługa')}
              />
            </RadioGroup>
          </div>
        )}

        {sourceMode === 'dictionary' && mode === 'add' && (
          <>
            <TextField
              label={t('Wyszukaj usługę')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              placeholder={t('Wpisz nazwę usługi')}
            />
            <div className={styles.results}>
              {isDictionaryFetching && (
                <div className={styles.loading}>
                  <CircularProgress size={20} />
                </div>
              )}
              {!isDictionaryFetching && dictionaryItems.length === 0 && (
                <div className={styles.empty}>{t('Brak wyników.')}</div>
              )}
              {!isDictionaryFetching &&
                dictionaryItems.map((item) => {
                  const isSelected = serviceId === item.id;
                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`${styles.resultItem} ${isSelected ? styles.resultItemSelected : ''}`}
                      onClick={() => handleSelectDictionaryItem(item)}>
                      <div className={styles.resultName}>{item.name}</div>
                      {item.taxPercent != null && (
                        <div className={styles.resultMeta}>
                          {t('VAT')}: {item.taxPercent}%
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
          </>
        )}

        {(sourceMode === 'adHoc' || mode === 'edit') && (
          <TextField
            label={t('Nazwa usługi')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus={mode === 'edit' || sourceMode === 'adHoc'}
          />
        )}

        {/* Podgląd wybranego elementu słownika (tylko w trybie add+dictionary). */}
        {mode === 'add' && sourceMode === 'dictionary' && selectedDictionaryItem && (
          <div className={styles.selectedPreview}>
            <div className={styles.selectedName}>{selectedDictionaryItem.name}</div>
            <div className={styles.selectedMeta}>
              {t('Wybrana usługa ze słownika.')}
            </div>
          </div>
        )}

        <div className={styles.priceRow}>
          <TextField
            label={t('Cena netto')}
            type="number"
            value={Number.isNaN(priceNet) ? '' : priceNet}
            onChange={(e) => setPriceNet(parseFloat(e.target.value))}
            size="small"
            inputProps={{ min: 0, step: 0.01 }}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1 }}
          />
          <TextField
            label={t('Stawka VAT (%)')}
            type="number"
            value={Number.isNaN(taxPercent) ? '' : taxPercent}
            onChange={(e) => setTaxPercent(parseFloat(e.target.value))}
            size="small"
            inputProps={{ min: 0, max: 100, step: 1 }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
        </div>

        <TextField
          label={t('Typ ceny')}
          select
          value={priceType}
          onChange={(e) => setPriceType(e.target.value as OfferServicePriceType)}
          size="small"
          fullWidth>
          <MenuItem value="TOTAL">{t('Cena całościowa')}</MenuItem>
          <MenuItem value="UNIT">{t('Cena jednostkowa')}</MenuItem>
        </TextField>

        <div className={styles.priceTypeHint}>
          <div
            className={
              priceType === 'TOTAL'
                ? styles.priceTypeRowActive
                : styles.priceTypeRow
            }>
            <strong>{t('Cena całościowa')}:</strong>{' '}
            {t('doliczana raz do pozycji, niezależnie od ilości produktu.')}
          </div>
          <div
            className={
              priceType === 'UNIT'
                ? styles.priceTypeRowActive
                : styles.priceTypeRow
            }>
            <strong>{t('Cena jednostkowa')}:</strong>{' '}
            {t('mnożona przez ilość produktu w pozycji (cena × ilość).')}
          </div>
        </div>

        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <div className={styles.actions}>
          <Button onClick={onClose} variant="text" disabled={isSubmitting}>
            {t('Anuluj')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isSubmitting ? <CircularProgress size={18} /> : submitLabel}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default AddOfferServiceModal;
